// What a notification is CALLED — shared by the in-app bell and the push.
//
// This mapping lived only in apps/app's liveNotifications.ts. Web push needs
// the same event→title table, and a second copy would drift: the bell would say
// "Rider dispatched" while the lock screen said something else for the same row.
// It lives in packages/logic because that is the one place both a React app and
// a Deno Edge Function can import from (it is vendored into supabase/functions).
//
// Bodies are NOT here. They are written per-event in notify.service.ts, where
// the PII-free rule lives — refs, amounts and times only, never a client name
// or address (data-wall.md).

/** Events the PRD §7.4 matrix defines. Unknown values degrade, never throw. */
export function notificationTitle(event: string): string {
  switch (event) {
    case "booking_confirmed":
      return "Booking confirmed";
    case "job_assigned":
      return "New job assigned";
    case "dispatch_assigned":
      return "Dispatch assigned";
    case "rider_dispatched":
      return "Rider dispatched";
    // 0389 — to the therapist and rider when their booking is cancelled.
    case "job_cancelled":
      return "Job cancelled";
    case "rider_arrived":
      return "Your therapist has arrived";
    case "session_complete":
      return "Session complete";
    case "cash_remittance_pending":
      return "Cash to receive";
    case "giftpass_redeemed":
      return "GiftPass redeemed";
    case "mileage_approval_needed":
      return "Approval needed";
    case "mileage_rate_decided":
      return "Mileage rate updated";
    case "kyc_submitted":
      return "ID submitted for review";
    case "credits_topped_up":
      // Written by SQL (0115) and pushed since 0260; the Credits ledger
      // already calls this a "Top-up".
      return "Credits top-up received";
    case "secure_sessions_reminder":
      // Same words the partner bell uses (liveNotifications.ts) — one event,
      // one title, whether it is read in the app or on a lock screen.
      return "Secure your sessions";
    case "therapist_sos":
      // 0273 — sent to her hub's Hub Managers and every admin. Says what it
      // is on a lock screen; the body carries her first name and the ref.
      return "SOS: a therapist needs help";
    // 0277 — to the therapist herself, about her own SOS. Names no one.
    case "therapist_sos_seen":
      return "Your SOS was seen";
    case "therapist_sos_resolved":
      return "Your SOS is closed";
    // 0363 — to the rider sent to collect a therapist who raised an SOS. The
    // body carries her first name only.
    case "sos_pickup_assigned":
      return "SOS: collect your therapist now";
    // 0278 — the live map's urgent alerts, now also sent to the bell.
    case "payment_issue":
      return "Payment issue";
    case "no_show_reported":
      return "No-show reported";
    case "booking_running_late":
      return "Booking running late";
    // 0393 — the Book Now line found someone.
    case "waitlist_found":
      return "A therapist is free";
    // 0321 — booking chat and support threads, pushed to the other
    // participant. PII-free on a lock screen, like every title here.
    case "chat_message":
      return "New message";
    case "support_message":
      return "Support update";
    default:
      return "Notification";
  }
}

/**
 * Where tapping a notification should land.
 *
 * Deliberately coarse — every client-facing event goes to the bookings list
 * rather than a specific booking. The notifications row carries no booking id,
 * only prose, and parsing a ref back out of a sentence to build a deep link is
 * exactly the kind of cleverness that breaks the first time the copy changes.
 *
 * STAFF events open the partner app, so they need partner paths — `/bookings`
 * does not exist there. They land on the role's HOME, which is what opening the
 * app would do anyway: the partner bell deliberately does not deep-link a job
 * (liveNotifications.ts — no rule says where it should go), and a push must not
 * invent one. The securing reminder is the exception because TSESSION-025
 * names its destination ("Link to Available Sessions").
 *
 * `rider_arrived` goes to a client AND a therapist with the same event name, so
 * it keeps the client path; the partner service worker sends any path outside
 * `/staff` to the staff entry instead.
 */
export function notificationPath(event: string): string {
  switch (event) {
    case "job_assigned":
      return "/staff/therapist";
    case "dispatch_assigned":
    case "sos_pickup_assigned":
      return "/staff/rider";
    case "secure_sessions_reminder":
      return "/staff/therapist/sessions";
    case "credits_topped_up":
      return "/credits";
    // 0393 — the queue page, which takes them on to confirm.
    case "waitlist_found":
      return "/book/waitlist";
    case "cash_remittance_pending":
    case "mileage_approval_needed":
    case "mileage_rate_decided":
      return "/";
    case "kyc_submitted":
      return "/kyc";
    case "therapist_sos":
      // Admin home, where the SOS alert stays until it is acknowledged.
      return "/";
    case "chat_message":
    case "support_message":
      // The client's chat home — where both booking chat and Live Support
      // live. Partner staff get re-pointed to `/staff/{role}` by the push
      // service, the same treatment every shared event gets: a notification
      // that lands them in the app rather than guessing a deep link.
      return "/chat";
    default:
      return "/bookings";
  }
}

// ─── admin side (0278) ──────────────────────────────────────────────────────

/**
 * How loudly an admin-side notification asks for attention.
 *
 *   urgent  someone may be in danger, or money moved wrong: red, pinned to the
 *           top of the bell while unread, toasted
 *   action  something is waiting for this person: amber, toasted
 *   info    news, nothing to do: neutral, bell only
 */
export type NotificationLevel = "urgent" | "action" | "info";

export function notificationLevel(event: string): NotificationLevel {
  switch (event) {
    case "therapist_sos":
    case "payment_issue":
    case "no_show_reported":
    case "booking_running_late":
      return "urgent";
    case "cash_remittance_pending":
    case "kyc_submitted":
    case "mileage_approval_needed":
      return "action";
    default:
      return "info";
  }
}

/** What a notification is about — the `subject_type` / `subject_id` of 0278. */
export interface NotificationSubject {
  type: string | null;
  id: string | null;
}

/**
 * Where a notification takes an ADMIN-APP user: the record when the row names
 * one, otherwise the page it belongs to. Admin routes only — the partner and
 * client apps keep `notificationPath`, which the push relay uses.
 *
 * The caller still checks the user may open the page (`canAccess`): a link
 * here is a suggestion, never a permission.
 */
export function adminNotificationHref(event: string, subject: NotificationSubject): string {
  const id = subject.id ? encodeURIComponent(subject.id) : null;
  if (subject.type === "booking" && id) return `/bookings?booking=${id}`;
  if (subject.type === "sos_alert" && id) return `/live-map?sos=${id}`;
  switch (event) {
    case "therapist_sos":
      return "/live-map";
    case "payment_issue":
      // No booking matched — the exception is worked from the Ops Dashboard.
      return "/";
    case "no_show_reported":
    case "booking_running_late":
      return "/bookings";
    case "cash_remittance_pending":
      return "/cash";
    case "kyc_submitted":
      return "/kyc";
    case "mileage_approval_needed":
    case "mileage_rate_decided":
      return "/mileage";
    default:
      return "/";
  }
}
